import { flow, makeObservable, action, observable, computed } from 'mobx';
import { GlobalStore, ApiResponse, PortalConfigurationService } from '@roc/feature-app-core';
import { LoanStore } from '@roc/feature-loans';
import { PointOfContactFormStore } from './pointOfContactFormStore';
import { PointOfContactFormStore as PointOfContactFormStoreMobileEnabled } from './pointOfContactFormStoreMobileEnabled';
import { MobileSnapDrawPointOfContactFormStore } from './mobileSnapPointOfContactForm';
import { DrawService } from '../../services/drawService';
import { PointOfContactService } from '../../services/pointOfContactService';
import { GENERIC_ERROR_MESSAGE } from '@roc/feature-utils';

export enum PointOfContactType {
  DRAWS = 'DRAWS',
  INTERNAL_VALUATION = 'INTERNAL_VALUATION',
}

export class PointOfContactStore {
  globalStore: GlobalStore;
  loanStore: LoanStore;
  pointOfContactFormStore: PointOfContactFormStore;
  pointOfContactDrawMobileFormStore: MobileSnapDrawPointOfContactFormStore;
  private drawService: DrawService;
  pointOfContactsList: any[];
  showAllFormValues: boolean = true;
  private pointOfContactService: PointOfContactService;
  private portalConfigurationService: PortalConfigurationService;
  isSoftCostOnlyDraw: boolean = false;
  isNewPointFormOfContactOpen: boolean = false;
  pointOfContacts: any[] = [];
  lenderInfo?: any;
  lenderPortalHost?: string;

  constructor(globalStore, loanStore) {
    this.globalStore = globalStore;
    this.loanStore = loanStore;
    this.drawService = new DrawService();
    this.pointOfContactFormStore = new PointOfContactFormStore(globalStore);
    this.pointOfContactService = new PointOfContactService();
    this.portalConfigurationService = new PortalConfigurationService();
    this.pointOfContactDrawMobileFormStore = new MobileSnapDrawPointOfContactFormStore(globalStore);
    this.pointOfContactsList = [];
    this.setShowAllFormValues();
    makeObservable(this, {
      getPointOfContact: flow,
      getPointOfContactsList: flow,
      pointOfContacts: observable,
      isNewPointFormOfContactOpen: observable,
      pointOfContactsList: observable,
      setIsNewPointOfContactOpen: action,
      savePointOfContactMobileSnap: flow,
      isSoftCostOnlyDraw: observable,
      setForm: action,
      fetchPointOfContacts: flow,
      setShowAllFormValues: action,
      showAllFormValues: observable,
      resetStore: action,
      setIsSoftCostOnlyDraw: action,
      pointOfContactFormStore: observable,
      pointOfContactDrawMobileFormStore: observable,
      isMobileSnapDrawSelected: computed,
      copyLink: action,
      lenderInfo: observable,
      fetchLenderInfo: flow,
      lenderPortalHost: observable,
      fetchLenderPortalHost: flow,
    });
  }
  isMobileSnapDrawEnabled() {
    const lenderInfo = this.lenderInfo ?? this.globalStore.lenderInfo;
    return lenderInfo?.hasMobileSnapEnabled || lenderInfo?.limitedFunctionalitiesList?.includes('isDrawMobileFlowEnabled');
  }
  setIsNewPointOfContactOpen(value: boolean) {
    this.isNewPointFormOfContactOpen = value;
  }
  setShowAllFormValues() {
    if (this.isMobileSnapDrawEnabled()) {
      this.showAllFormValues = this.pointOfContactFormStore.form.fields.inspectionProcess.value !== 'Snap';
    }
    else {
      this.showAllFormValues = true;
    }
  }
  setIsSoftCostOnlyDraw(value) {
    if (value) {
      this.pointOfContactFormStore.onFieldChange('inspectionProcess', null);
      this.pointOfContactFormStore.onFieldChange('addPointOfContact', 'N');
    }
    this.isSoftCostOnlyDraw = value;
  }
  setForm() {
    if (this.isMobileSnapDrawEnabled()) {
      this.pointOfContactFormStore = new PointOfContactFormStoreMobileEnabled(this.globalStore);
    }
  }
  *fetchPointOfContacts(loanId: number) {
    try {
      const response = yield this.pointOfContactService.getPointOfContactsByLoanId(
        loanId
      );
      const data = response.data.data;
      this.pointOfContacts = data;
      const latestPointOfContact = [...this.pointOfContacts].sort(
        (a, b) => b.pointOfContactId - a.pointOfContactId
      )?.[0];
      if (latestPointOfContact) {
        this.loadPointOfContact(latestPointOfContact);
      }
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while fetching point of contacts.',
      });
    }
  }
  selectPointOfContact(pointOfContactId: number) {
    this.pointOfContactFormStore.onFieldChange(
      'pointOfContactId',
      pointOfContactId
    );
    this.resetPointOfContactData();
    if (pointOfContactId) {
      const poc = this.pointOfContacts.find(
        poc => poc.pointOfContactId === pointOfContactId
      );
      this.pointOfContactFormStore.loadForm(poc);
    }
  }
  resetPointOfContactData() {
    this.pointOfContactFormStore.form.fields.firstName.value = '';
    this.pointOfContactFormStore.form.fields.lastName.value = '';
    this.pointOfContactFormStore.form.fields.phoneNumber.value = '';
    this.pointOfContactFormStore.form.fields.email.value = '';
    this.pointOfContactFormStore.form.fields.comments.value = '';
  }
  loadPointOfContact(pointOfContact: any) {
    this.pointOfContactFormStore.loadForm({
      pointOfContactId: pointOfContact.pointOfContactId,
      firstName: pointOfContact.firstName,
      lastName: pointOfContact.lastName,
      phoneNumber: pointOfContact.phone,
      email: pointOfContact.email,
      comments: '',
    });
  }
  *getPointOfContact(loanId: string) {
    try {
      const response: ApiResponse = yield this.drawService.getPointOfContact(
        loanId
      );
      const data = response.data?.data || {};
      this.pointOfContactFormStore.form.fields.firstName.value = data?.contactFirstName || '';
      this.pointOfContactFormStore.form.fields.lastName.value = data?.contactLastName || '';
      this.pointOfContactFormStore.form.fields.phoneNumber.value = data?.contactPhone || '';
      this.pointOfContactFormStore.form.fields.email.value = data?.contactEmail || '';
      this.pointOfContactFormStore.form.fields.comments.value = data?.contactComment || '';
      if (JSON.parse(data?.drawRequestJsonContent)?.inspectionProcess) {
        this.pointOfContactFormStore.form.fields.inspectionProcess.value = JSON.parse(data?.drawRequestJsonContent)?.inspectionProcess;
      }
    } catch (e) {
      console.log(e);
    }
  }
  *getPointOfContactsList(loanId: string, filters) {
    try {
      const response: ApiResponse = yield this.drawService.getPointOfContactsList(
        loanId,
        filters.type,
        filters.propertyId,
        filters.drawId,
        filters.taskId,
      );
      this.pointOfContactsList = response.data?.data || [];
    } catch (e) {
      console.log(e);
    }
  }

  savePointOfContact(drawId: number, pointOfContact) {
    this.drawService.updatePointOfContact(drawId, pointOfContact);
  }

  *savePointOfContactMobileSnap(loanId: number, filters, pointOfContact) {
    try {
      const resp = yield this.drawService.createPointOfContactMobileSnap(loanId, pointOfContact);
      if (resp?.data?.status === "OK") {
        this.isNewPointFormOfContactOpen = false;
        this.pointOfContactDrawMobileFormStore.resetStore();
        yield this.getPointOfContactsList(this.loanStore?.loanDetails?.loanId, filters);
      }
    } catch (e) {
      console.error('Error saving point of contact:', e);
    }
  }
  resetStore = () => {
    this.pointOfContactFormStore.resetStore();
  }

  copyLink(poc) {
    navigator.clipboard.writeText(poc?.inviteLink);
    this.globalStore.notificationStore.showSuccessNotification({
      message: 'Link has been copied to clipboard',
    });
  }

  *fetchLenderInfo(lenderId){
    try {
      const response = yield this.pointOfContactService.getLenderById(lenderId);
      this.lenderInfo = response.data;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *fetchLenderPortalHost(portalId) {
    try {
      const response = yield this.portalConfigurationService.getPortalConfigurationById(portalId);
      this.lenderPortalHost = response?.data?.data?.host ?? '';
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  get isMobileSnapDrawSelected() {
    return (
      this.isMobileSnapDrawEnabled() &&
      this.pointOfContactFormStore.form.fields.inspectionProcess.value ===
      'Snap'
    );
  }
}
